import type { Workspace } from '@/stores/workspace.store';

/**
 * Aturan memilih workspace untuk PWA Absensi (tiket Silvi/Voiz, 4 Agu 2026).
 *
 * Kasusnya: Silvi punya workspace PRIBADI (Absensi mati) dan juga anggota workspace
 * KANTOR (Absensi aktif). GET /workspaces dulu tanpa ORDER BY (urut UUID) dan PWA
 * memilih `tersimpan || list[0]` → jatuh ke workspace pribadi → layar "Plugin belum
 * aktif… hubungi owner". Dia owner-nya, jadi Absensi dia aktifkan DI SITU, dan
 * clock-in berikutnya masuk ke workspace pribadi — kantor tidak pernah melihatnya.
 *
 * Aturan (berurutan):
 *  1. Pilihan tersimpan (localStorage) DIPERTAHANKAN selama workspace-nya masih
 *     ada DAN (Absensi aktif di sana ATAU tidak ada workspace lain yang Absensinya
 *     aktif). Pilihan tersimpan yang Absensinya mati padahal ada workspace lain
 *     yang aktif dianggap basi → pilih ulang (langkah 2).
 *  2. Pilih otomatis:
 *     a. kandidat = workspace yang Absensinya aktif; tidak ada satu pun → semua;
 *     b. utamakan workspace yang BUKAN milik user (workspace kantor/tempat dia
 *        bekerja), baru workspace miliknya sendiri;
 *     c. seri → urutan dari server (GET /workspaces: created_at ASC, deterministik).
 *
 * User tetap bisa pindah manual kapan saja (switcher di header & kartu absen);
 * pilihan manual yang Absensinya aktif selalu dihormati oleh langkah 1.
 */

export const ATTENDANCE_PLUGIN = 'attendance';

export function hasAttendance(ws: Pick<Workspace, 'enabledPlugins'> | null | undefined): boolean {
    return !!ws?.enabledPlugins?.includes(ATTENDANCE_PLUGIN);
}

export function isOwnedBy(ws: Pick<Workspace, 'ownerId'> | null | undefined, userId: string | null | undefined): boolean {
    return !!ws && !!userId && ws.ownerId === userId;
}

/** Urutkan: bukan-milik-user dulu, lalu milik user; urutan server dipertahankan (sort stabil). */
function preferNotOwned<T extends Workspace>(list: T[], userId: string | null | undefined): T[] {
    return [...list].sort((a, b) => Number(isOwnedBy(a, userId)) - Number(isOwnedBy(b, userId)));
}

export function pickDefaultWorkspace<T extends Workspace>(
    list: T[],
    opts: { persistedId?: string | null; userId?: string | null } = {},
): T | null {
    if (list.length === 0) return null;
    const anyEnabled = list.some(hasAttendance);

    const persisted = opts.persistedId ? list.find((w) => w._id === opts.persistedId) : undefined;
    if (persisted && (hasAttendance(persisted) || !anyEnabled)) return persisted;

    const candidates = anyEnabled ? list.filter(hasAttendance) : list;
    return preferNotOwned(candidates, opts.userId)[0] ?? null;
}

/** Workspace LAIN tempat Absensi aktif — untuk tombol "pindah" di layar Absensi-mati. */
export function otherAttendanceWorkspaces<T extends Workspace>(
    list: T[],
    currentId: string | null | undefined,
    userId: string | null | undefined,
): T[] {
    return preferNotOwned(list.filter((w) => w._id !== currentId && hasAttendance(w)), userId);
}
